/**
 * -------------------------------------------------------
 * Navigation Composable
 * -------------------------------------------------------
 *
 * Author       : Harvinder Singh
 * Updated On   : 26 Feb 2026
 *
 * Description:
 * ------------
 * Centralized navigation builder for Shell + MFE routing.
 *
 * Responsibilities:
 * 1. Filter navigation items based on:
 *    - Product access
 *    - Feature access
 *    - User roles
 * 2. Remove parents if all children are filtered out
 * 3. Special handling for `onsales: true`
 *      - Keep parent visible even if all children hidden
 *      - Hide submenu
 *      - Add `isPremium: true`
 * 4. Attach navigation handlers at ALL nesting levels
 * 5. Handle Shell vs MFE routing transparently
 *
 * Returns:
 *  - navigation: fully resolved navigation tree
 *  - navigate: shared navigation handler
 *
 * NOTE:
 * -----
 * RAW_NAVIGATION remains PURE DATA.
 * All logic lives inside this composable.
 * -------------------------------------------------------
 */

import { computed, ref } from "vue";
import { useUserStore } from "@/lib/stores";
import { navigateToUrl, unloadApplication } from "single-spa";
import { useRouter } from "vue-router";
import { getClusterProductStatus } from "@/lib/utils/cluster-products";
import { isProvisioningIncomplete } from "@/lib/utils/accounts";
import { splitKey } from "@/lib/utils/utilityFunctions";
import netoIcon from "@/assets/neto_nav_logo.svg";

/* -------------------------------------------------------
   TEMPORARY Split.io gate for Merchandising (Unified)
------------------------------------------------------- */
const MHC_UNIFIED_FLAG = "mhc_new_dashboard";

/** navigation recomputes once the Split treatment resolves. */
const showMhcUnifiedNav = ref(false);
let mhcFlagRequested = false;

const loadMhcUnifiedSplitFlag = (accountId: number): void => {
  if (!accountId || mhcFlagRequested) return;

  mhcFlagRequested = true;

  splitKey(MHC_UNIFIED_FLAG, accountId)
    .then((enabled: boolean) => { showMhcUnifiedNav.value = enabled; })
    .catch(() => { mhcFlagRequested = false; });
};

/* -------------------------------------------------------
   ACCESS & PREVIEW RULES
------------------------------------------------------- */

/**
 * Maps scope → product key used by backend access checks
 */
const ACCESS_BY_SCOPE: Record<string, string> = {
  accounts: "marketing",
  commerce: "commerce",
  service: "service",
  neto: "neto",
  mhc: "mhc",
  merchandise: "mhc",
};

/* -------------------------------------------------------
   RAW NAVIGATION CONFIGURATION
------------------------------------------------------- */
/**
 * IMPORTANT:
 * ----------
 * - This config is PURE DATA
 * - No logic, no conditions
 * - All filtering happens later in the composable
 *
 * Structure:
 * -----------
 * [
 *   [ section1Items ],
 *   [ section2Items ],
 *   ...
 * ]
 */
const RAW_NAVIGATION = [
  [
    { 
      id: "dashboard", 
      title: "Dashboard", 
      icon: "dashboard",
      scope: "accounts",
      products: ["accounts", "commerce","service"], 
      routeName: "/dashboard" 
    },
    {
      id: "analytics",
      title: "Analytics",
      icon: "timeline",
      submenu: [
        { title: "Monthly Totals", scope: "commerce", routeName: "/reports/monthly-totals", features:['monthly_totals'] },
        { title: "Sales by Order", scope: "commerce", routeName: "/reports/sales-by-order", features:['sales_by_order'] },
        { title: "Dispatched Orders", scope: "commerce", routeName: "/reports/dispatched-orders", features:['dispatched_orders'] },
        { title: "Sales Summary", scope: "commerce", routeName: "/reports/sales-summary", features:['sales_summary'] },
        { title: "eRFM Report", scope: "accounts", routeName: "/erfm_report", userRole:['reports'], features:['erfm_reports'] },
        { title: "Campaign Reports", scope: "accounts", routeName: "/reports", userRole:['reports'],features:['custom_reports']  },
        { title: "Recurring Campaign Reports", scope: "accounts", routeName: "/reports/recurring_campaign_report",userRole:['reports'],features:['custom_reports']  },
        { title: "A/B Campaign Reports", scope: "accounts", routeName: "/ab_reports",userRole:['reports'],features:['custom_reports']  },
        { title: "Test Campaign Reports", scope: "accounts", routeName: "/reports/test_reports", userRole:['reports'],features:['custom_reports']  },
        { title: "Website Reports", scope: "accounts", routeName: "/website_reports", userRole:['reports'], features:['web_tracking']  },
        { title: "Journey Reports", scope: "accounts", routeName: "/reports/journeys", userRole:['reports'],features:['custom_reports']  },
        { title: "Custom Reports", scope: "accounts", routeName: "/analytics/custom_reports", userRole:['reports'],features:['custom_reports']  },
        { title: "Transactional Email Reports", scope: "accounts", routeName: "/transactional_campaigns/reports", userRole:['reports'],features:['custom_reports']  },
        { title: "Log Inspector", scope: "accounts", routeName: "/get_logs", userRole:['reports']  },
      ],
    },
  ],
  [
    {
      id: "contacts",
      title: "CDP",
      icon: "people_outline",
      submenu: [
        { title: "All Contacts", scope: "accounts", products: ["accounts", "commerce","service"], routeName: "/contacts", userRole:['contacts'], features:['contacts'] },
        { title: "Contact Lists", scope: "accounts", products: ["accounts","commerce","service"], routeName: "/lists", userRole:['contacts'], features:['lists'] },
        { title: "Segments", scope: "accounts", routeName: "/segments", userRole:['contacts'], features:['segments']},
        { title: "Contact Fields", scope: "accounts", products: ["accounts","commerce","service"], routeName: "/custom_fields", userRole:['contacts'], features:['custom_field'] },
        { title: "Contact Tags", scope: "accounts", products: ["accounts","commerce","service"], routeName: "/tags", userRole:['contacts'], features:['tags']},
        { title: "Relational Tables", scope: "accounts", routeName: "/relational_tables", userRole:['contacts'], features:['relational_data'] },
        { title: "SQL Queries", scope: "accounts", routeName: "/relational_queries", userRole:['contacts'], features:['sql_queries'] },
        { title: "Secure Lists", scope: "accounts", routeName: "/secure_lists", userRole:['contacts'], features:['secure_suppression_lists'] },
        { title: "Web Tracking", scope: "accounts", routeName: "/websites", userRole:['forms'], features:['web_tracking'] },
      ],
    },
    {
      id: "products",
      title: "Products",
      icon: "inventory_2",
      submenu: [
        { title: "Product Recommendations", scope: "accounts", routeName: "/product_recommendations", features:['product_recommendations'] }, 
        { title: "Products", scope: "commerce", routeName: "/products", features:['skus'] },
        { title: "Product Tax Category", scope: "commerce", routeName: "/products/tax-categories", features:['product_tax_category'] },
        { title: "Collections", scope: "commerce", routeName: "/products/collections", features:['collections'] },
        { title: "Inventory", scope: "commerce", routeName: "/inventory", features:['inventory_locations'] },
        { title: "Reservations", scope: "commerce", routeName: "/inventory/reservations", features:['reservations'] },
      ],
    },
  ],
  [
    { 
      id: "Marketing", 
      title: "Marketing", 
      icon: "Campaign", 
      scope: "marketing", 
      onsales: true,
      submenu: [
        {
          id: "campaigns",
          title: "Campaigns",
          submenu: [
            { title: "Email Campaigns", scope: "accounts", routeName: "/campaigns", userRole:['campaigns'],features:['campaigns'], showAsDirect: true},
            { title: "Text Campaigns", scope: "accounts", routeName: "/sms_campaigns", userRole:['mobile'], features:['sms_campaigns'], showAsDirect: true },
            { title: "Transactional Email", scope: "accounts", routeName: "/transactional_campaigns", userRole:['campaigns'], features:['campaigns'], showAsDirect: true },
            { title: "Transactional SMS", scope: "accounts", routeName: "/transactional_sms_campaigns", userRole:['mobile'], features:['sms_campaigns'], showAsDirect: true},
            { title: "Campaign Tags", scope: "accounts", routeName: "/ctags", userRole:['campaigns'], features:['ctags'], showAsDirect: true },
          ],
        },
        {
          id: "acquisition",
          title: "Acquisition",
          submenu: [
            { title: "Acquisition Forms", scope: "accounts", routeName: "/acquisition/forms", userRole:['forms'], features:['acquisition_builder'] , showAsDirect: true },
            { title: "Landing Pages", scope: "accounts", routeName: "/landing_pages", features:['landing_pages'], showAsDirect: true },
            { title: "Lead Ads", scope: "accounts", routeName: "/social_leads", features:['lead_ads'], showAsDirect: true},
            { title: "Signup Forms (Old)", scope: "accounts", routeName: "/forms", userRole:['forms'], features:['sign_up_forms'], showAsDirect: true }, 
            { title: "Surveys", scope: "accounts", routeName: "/surveys", userRole:['surveys'], features:['surveys'], showAsDirect: true },
          ],
        },
        {
          id: "automation",
          title: "Automation",
          submenu: [
            { title: "Journeys", scope: "accounts", routeName: "/journeys", features:['journeys'], showAsDirect: true },
            { title: "Data Journeys", scope: "accounts", routeName: "/data_journeys", features:['data_journeys'], showAsDirect: true },
          ],
        },
        {
          id: "content",
          title: "Content",
          submenu: [
            { title: "Email Content", scope: "accounts", routeName: "/contents", userRole:['contents'] , features:['content_builder'], showAsDirect: true  },
            { title: "Dynamic Content", scope: "accounts", routeName: "/dynamic_contents", userRole:['contents'],features:['dynamic_content'], showAsDirect: true  },
            { title: "Image Library", scope: "accounts", routeName: "/folders", userRole:['contents'], showAsDirect: true  },
            { title: "Footer Management", scope: "accounts", routeName: "/footers", userRole:['contents'], features:['footer_management'], showAsDirect: true  },
            { title: "Optimise on Open", scope: "accounts", routeName: "/image_groups", userRole:['contents'], features:['optimize_on_open'], showAsDirect: true  },
            { title: "Content Feeds", scope: "accounts", routeName: "/content_feeds", userRole:['contents'], features:['content_feeds'], showAsDirect: true  },
            { title: "Coupon Banks", scope: "accounts", routeName: "/coupon_banks", userRole:['contents'], features:['coupon_bank'], showAsDirect: true},
            { title: "Preference Management", scope: "accounts", routeName: "/preference_pages", userRole:['contacts'], showAsDirect: true },
            { title: "Countdown Timer", scope: "accounts", routeName: "/live_content_images", userRole:['contents'], features:['count_down_timer'], showAsDirect: true  },
          ],
        },
      ]
    },
    { 
      id: "Commerce", 
      title: "Commerce", 
      icon: "shopping_bag", 
      scope: "commerce",
      onsales: true,
      submenu: 
        [
        {
          id: "orders",
          title: "Orders",
          submenu: [
            { title: "Sales Orders", scope: "commerce", routeName: "/orders", showAsDirect: true },
            { title: "Draft Orders", scope: "commerce", routeName: "/orders/draft", features:['draft_orders'], showAsDirect: true },
            { title: "Fulfillment", scope: "commerce", routeName: "/fulfillment", features:['fulfillment'], showAsDirect: true },
          ],
        },
        {
          id: "promos",
          title: "Promos & Coupons",
          submenu: [
            { title: "Promotions", scope: "commerce", routeName: "/promotions", features:['promotions'], showAsDirect: true },
            { title: "Custom Gift Cards", scope: "commerce", routeName: "/gift-cards", features:['custom_gift_cards'], showAsDirect: true },
            { title: "Purchasable Gift Cards", scope: "commerce", routeName: "/gift-cards/Purchasable", features:['purchasable_gift_cards'], showAsDirect: true },
          ],
        },
        { title: "Sales Channels", scope: "commerce", routeName: "/sales-channel" },
      ]
    },
    { 
      id: "mhc",
      title: "Merchandising", 
      icon: "mdi-shape-outline",
      products: ["mhc"],
      routeName: "/merchandising/dashboard",
    },
    {
      id: "mhc-unified",
      title: "Merchandising (Unified)",
      icon: "mdi-shape-outline",
      products: ["mhc"],
      scope: "merchandise",
      routeName: "/stores",
    },
    {
      id: "retail",
      title: "Retail",
      icon: "mdi-store-outline",
      onsales: true,
      isPremium: true,
      routeName: "/preview/retail",
    },
    {
      id: "Service",
      title: "Service",
      icon: "headphones",
      scope: "service",
      onsales: true,
      submenu: [
        { title: "Tickets", scope: "service", routeName: "/tickets", features:['tickets'] },
      ],
    },
  ],
  [
    { 
      id: "neto",
      title: "Neto", 
      iconSVG: netoIcon,
      products: ["neto"],
      routeName: "/neto/dashboard",
    },
  ],
  [
    {
      id: "da-vinci",
      title: "Da Vinci AI",
      icon: "mdi-creation-outline",
      isNew: true,
      submenu: [
        {
          id: "da-vinci-chatbots",
          title: "Chatbots",
          scope: "service",
          features:['da_vinci_customer_agent'],
          routeName: "/chatbots",
        },
      ],
    },
  ],
  [
    { id: "integrations", title: "Apps", icon: "integration_instructions", scope: "accounts", products: ["accounts", "service"], routeName: "/integrations" },
  ],
];

/* -------------------------------------------------------
   COMPOSABLE
------------------------------------------------------- */

export function useNavigation() {
  const userStore = useUserStore();
  const router = useRouter();

  /* -------------------------------------------------------
     STEP 1 – PRODUCT / SCOPE ACCESS
  ------------------------------------------------------- */

  const hasItemAccess = (item: any): boolean => {
    // Priority 1 – Explicit product list
    if (Array.isArray(item.products) && item.products.length) {
      return item.products.some((p: string) => {
        const product = ACCESS_BY_SCOPE[p] ?? p;
        return (
          userStore.getProductAccess(product) &&
          userStore.getProductAccessOnRoles(product)
        );
      });
    }

    // Priority 2 – Scope based
    if (item.scope) {
      const product = ACCESS_BY_SCOPE[item.scope] ?? item.scope;
      return (
        userStore.getProductAccess(product) &&
        userStore.getProductAccessOnRoles(product)
      );
    }

    return true;
  };

  /* -------------------------------------------------------
     STEP 2 – FEATURE ACCESS
  ------------------------------------------------------- */

  const featureKeySet = computed(
    () =>
      new Set(
        (userStore.getPackageFeatures || []).map(
          (f: any) => f.feature_key
        )
      )
  );

  const buildUrl = (item: any) => {
    if (!item?.routeName) return undefined;
  
    let path = item.routeName;
  
    if (item.scope) {
      path = `/${item.scope}/${userStore.getAccountId}${item.routeName}`;
    }
  
    return path;
  };

  /**
   * Supports:
   * - "a"
   * - ["a","b"] (ALL required)
   * - ["a", ["b","c"]] (ANY group)
   */
  const hasFeatureAccess = (features?: any[]): boolean => {
    if (!features || !features.length) return true;

    return features.some(feature => {
      if (typeof feature === "string") {
        return featureKeySet.value.has(feature);
      }

      if (Array.isArray(feature)) {
        return feature.every(f =>
          featureKeySet.value.has(f)
        );
      }

      return false;
    });
  };

  /* -------------------------------------------------------
     STEP 3 – ROLE ACCESS
  ------------------------------------------------------- */

  const userRoleSet = computed(
    () => new Set(userStore.getRoles || [])
  );

  const hasRoleAccess = (roles?: string[]): boolean => {
    if (!roles || !roles.length) return true;
    return roles.some(role => userRoleSet.value.has(role));
  };

  /* -------------------------------------------------------
     FINAL ACCESS CHECK
  ------------------------------------------------------- */

  const canShowItem = (item: any): boolean => {
    if (!hasItemAccess(item)) return false;
    if (!hasFeatureAccess(item.features)) return false;
    if (!hasRoleAccess(item.userRole)) return false;
    return true;
  };

  /* -------------------------------------------------------
     ROUTING DECISION
  ------------------------------------------------------- */

  const isShellRoute = (path: string): boolean => {
    return (
      !path.startsWith("/accounts") &&
      !path.startsWith("/commerce") &&
      !path.startsWith("/service") &&
      !path.startsWith("/merchandise")
    );
  };

  const navigate = async (item: any) => {
    if (!item?.routeName) return;

    let path = item.routeName;

    if (item.scope) {
      path = `/${item.scope}/${userStore.getAccountId}${item.routeName}`;
    }

    if (isShellRoute(path)) {
      router.push(path);
      return;
    }

    if (item.scope === "merchandise") {
      // already on the same path —> no-op
      if (window.location.pathname === path) return;

      await unloadApplication("merchandise", { waitForUnmount: false });
    }

    navigateToUrl(path);
  };

  const toPreviewSlug = (title: string): string =>
    title
      .toLowerCase()
      .trim()
      .replace(/[^a-z0-9\s-]/g, "")
      .replace(/\s+/g, "-")
      .replace(/-+/g, "-");

  /**
   * Recursively marks a navigation tree as premium.
   *
   * Behavior:
   * - Adds `isPremium` to every menu item.
   * - Preserves the existing submenu hierarchy.
   * - Attaches `onClick` only to leaf nodes (items without children).
   * - Parent items remain expandable and do not receive a click handler.
   * - All leaf nodes navigate to the same premium preview page using
   *   the top-level product ID.
   */
 
  const addPremiumProps = (
    menuItem: any,
    productId: string
  ): any => {
    const hasSubmenu =
      Array.isArray(menuItem.submenu) && menuItem.submenu.length > 0;
    const previewPath = hasSubmenu
      ? undefined
      : `/preview/${productId}/${toPreviewSlug(menuItem.title)}`;
 
    return {
      ...menuItem,
      isPremium: true,
      scope: undefined,
      routeName: previewPath,
      onClick: hasSubmenu
        ? undefined
        : () => navigate({ ...menuItem, routeName: previewPath, scope: undefined }),
      submenu: menuItem.submenu?.map((child: any) =>
        addPremiumProps(child, productId)
      ),
    };
  };

  /* -------------------------------------------------------
     CHATBOT ACCESS (Da Vinci toggle + MSC access + Free Trial)
  ------------------------------------------------------- */

  /**
   * Chatbot is fully usable only when ALL of the following hold:
   * - Account is not a free trial account
   * - Account has MSC (service) product access
   * - Da Vinci Customer Agent add-on is enabled (`da_vinci_customer_agent`)
   *
   * Any other combination renders the item with a crown and redirects
   * to the Da Vinci "Talk to Sales" page instead of the real Chatbots MFE.
   */
  const CHATBOT_TALK_TO_SALES_PATH = "/preview/da-vinci/chatbots";

  const hasChatbotAccess = (item: any): boolean => {
    if (userStore.getIsTrailAccount) return false;

    const hasMscAccess =
      userStore.getProductAccess("service") &&
      userStore.getProductAccessOnRoles("service");
    if (!hasMscAccess) return false;

    return hasFeatureAccess(item.features);
  };

  const processChatbotItem = (item: any): any => {
    if (hasChatbotAccess(item)) {
      return {
        ...item,
        href: buildUrl(item),
        onClick: () => navigate(item),
      };
    }

    return {
      ...item,
      isPremium: true,
      scope: undefined,
      routeName: CHATBOT_TALK_TO_SALES_PATH,
      onClick: () => navigate({ routeName: CHATBOT_TALK_TO_SALES_PATH }),
    };
  };

  /* -------------------------------------------------------
     RECURSIVE NAVIGATION BUILDER
  ------------------------------------------------------- */

  /**
   * Recursively processes navigation item.
   *
   * Handles:
   * - Deep nested submenu
   * - Access filtering
   * - onsales → isPremium logic
   * - Click handler attachment
   */

  const processItem = (item: any): any | null => {
    if (item.id === "da-vinci-chatbots") {
      return processChatbotItem(item);
    }

    // TODO: remove once we switch to the admin console gating (NEW_MHC_SPLIT_GATE)
    if (item.id === "mhc-unified") {
      if (!canShowItem(item)) return null;

      loadMhcUnifiedSplitFlag(userStore.getAccountId);
      if (!showMhcUnifiedNav.value) return null;
    }

    const hasSubmenu = Array.isArray(item.submenu);

    let processedSubmenu: any[] | undefined;

    // 🔁 Process children first (deep recursion)
    if (hasSubmenu) {
      processedSubmenu = item.submenu
        .map(processItem)
        .filter(Boolean);
    }

    // if the account is a marketing account, check if the package name includes "aff_"
    const marketingPackage:any = userStore.getTrailPackageFeatures
    if(marketingPackage?.mmc?.package_name.includes("aff_")){
      if(item.onsales && item.id != "Marketing"){
        return null;
      }
    }

    const hasVisibleChildren =
      processedSubmenu && processedSubmenu.length > 0;

    const selfVisible = canShowItem(item);

    /* -----------------------------------------------
       CASE 1 – Item HAS submenu
    ----------------------------------------------- */
    if (hasSubmenu) {
      // If at least one child visible → show parent normally
      if (hasVisibleChildren) {
        return {
          ...item,
          submenu: processedSubmenu,
          href: buildUrl(item),
          onClick: item.routeName
            ? () => navigate(item)
            : undefined,
        };
      }

      // Special rule: onsales parent
      if (item.onsales) {

        // If cluster does NOT contain this product → remove item
        const { exists, status } = getClusterProductStatus(item.id);

        // if the account provisioning is not complete, redirect to account setup
        if (isProvisioningIncomplete()) {
          return {
            ...item,
            submenu: undefined,
            isPremium: true,
            onClick: () => router.push("/account-setup"),
          };
        }
        
        // if the cluster does not contain this product, remove the item
        if (!exists) {
          return null;
        }
         // ✅ Product exists & ACTIVE → allow normal navigation
        if (status === "active") {
          if(userStore.getProductAccess((item.id).toLowerCase()) && !userStore.getProductAccessOnRoles((item.id).toLowerCase())){
            return null;
          }
          return {
            ...addPremiumProps(item, item.id),
          };
        }

        // 🟡 Product exists but INACTIVE → premium preview mode
        if (status === "inactive") {
          return {
            ...item,
            submenu: undefined,
            isPremium: true,
            onClick: () => {
              if (
                !userStore.getProductAccess("marketing") ||
                !userStore.getProductAccessOnRoles("marketing")
              ) {
                router.push(`/coming-soon/${item.id}`);
              } else {
                navigateToUrl(`/coming-soon/${item.id}`);
              } 
            },
          };
        }
      }

      // No children + not onsales → remove
      return null;
    }

    /* -----------------------------------------------
       CASE 2 – Leaf node
    ----------------------------------------------- */
    if (!selfVisible) return null;

    return {
      ...item,
      href: buildUrl(item),
      onClick: item.routeName
        ? () => navigate(item)
        : undefined,
    };

  };


  /* -------------------------------------------------------
     FINAL NAVIGATION TREE
  ------------------------------------------------------- */

  const navigation = computed(() =>
    RAW_NAVIGATION
      .map(section =>
        section
          .map(processItem)
          .filter(Boolean)
      )
      .filter(section => section.length > 0)
  );

  /* -------------------------------------------------------
     PUBLIC API
  ------------------------------------------------------- */

  return {
    navigation,
    navigate,
  };
}